"use client"

import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { FaCircleInfo, FaPlus } from 'react-icons/fa6';
import Image from 'next/image';
import { cn } from '@/utils/utils';
import { captureClientEvent } from "@/lib/telemetry-client";
import fresenbruch from "@/components/gfx/fresenbruch.jpg";
import wellnessCamp from "@/components/gfx/wellness-camp.jpg";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const faqs = [
    {
        question: "Wie kann ich reservieren?",
        answer: `Reservierungen sind leider nicht möglich. Ihr fahrt einfach auf den Platz, sucht euch einen freien Stellplatz und bezahlt erst dann online. Es sind fast immer Plätze frei - beste Anreisezeit ist gegen 10 Uhr. In ${new Date().getFullYear() - 2002} Jahren mussten wir fast Niemanden abweisen. Ihr seid jederzeit herzlich willkommen!`
    },
    {
        question: "Was kostet der Stellplatz pro Person?",
        answer: "Alle unsere Preise verstehen sich pro Fahrzeug, nicht pro Person. Die Kurtaxe wird bei der Online-Bezahlung automatisch mit abgerechnet (nicht im Ticketpreis enthalten).",
        link: { href: "#preise", text: "Zur Preisübersicht" }
    },
    {
        question: "Kann ich mich bei Euch an den Strom anschliessen?",
        answer: "Ja! Wir haben 40 Selbst-Service Stromplätze mit typischen CEE 3-Pin Camping-Stecker Anschluss. Der Strom kostet 1,- € pro Kilowatt-Stunde und kann mit Münzgeld bezahlt werden.",
        note: "Hinweis: Im Winter (Oktober-April) ist kein Strom verfügbar."
    },
    {
        question: "Welches Ticket brauche ich für mein Fahrzeug?",
        answer: "Das Übernachtungsticket gilt für alle Fahrzeuge, in denen übernachtet wird: Wohnmobile (WOMOs), Campervans, Bullis, PKW mit Dachzelt oder Schlafausbau. Wer einen Wohnwagen/Caravan dabei hat, wählt das Ticket 'PKW + Caravan'. Wer nur tagsüber parkt (ohne Übernachtung), benötigt ein Tagesticket.",
        link: { href: "#preise", text: "Zur Preisübersicht" }
    },
    {
        question: "Können wir unseren Hund mitbringen?",
        answer: "Ja, sehr gerne! Bitte beachtet die Hundegebühr von 8,- € pro Hund und Nacht."
    },
    {
        question: "Muss ich bei Euch Kurtaxe bezahlen?",
        answer: "In Zingst sind alle Unterkünfte kurtaxenabgabepflichtig, das heisst auch auf unserem Stellplatz fällt die Kurtaxe an. Darum müsst ihr euch aber nicht kümmern: Die Kurtaxe wird bei der Online-Bezahlung automatisch mit abgerechnet (zusätzlich zum Ticketpreis). Die Gästekarte wird im Anschluss automatisch per E-Mail zugesendet."
    },
    {
        question: "Kann man am Platz die Toilettenkassette entleeren?",
        answer: "Bei uns gibt es keine Entsorgung, aber in naher Umgebung gibt es öffentliche Entsorgungs-Stationen:",
        hasPopovers: true
    }
];

const FAQ = ({ className }: { className?: string }) => {
    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    return (
        <div ref={ref} className={cn("mx-auto max-w-3xl px-4", className)}>
            <Accordion
                type="single"
                collapsible
                defaultValue="item-0"
                className="space-y-4"
                onValueChange={(value) => {
                    if (!value) return;
                    const index = Number(value.replace("item-", ""));
                    const question = Number.isNaN(index) ? value : (faqs[index]?.question ?? value);
                    captureClientEvent("faq_item_opened", { item: question });
                }}
            >
                {faqs.map((faq, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                        <AccordionItem
                            value={`item-${index}`}
                            className="group overflow-hidden rounded-2xl border-0 bg-white/10 backdrop-blur-sm transition-all duration-300 data-[state=open]:bg-white/15"
                        >
                            <AccordionTrigger className="px-6 py-5 text-left text-lg font-semibold hover:no-underline [&[data-state=open]>svg]:rotate-45 [&>svg]:transition-transform [&>svg]:duration-300">
                                <span className="pr-4">{faq.question}</span>
                            </AccordionTrigger>
                            <AccordionContent className="px-6 pb-6">
                                <div className="text-slate-300">
                                    <p className="leading-relaxed">{faq.answer}</p>

                                    {faq.link && (
                                        <a
                                            href={faq.link.href}
                                            onClick={() => captureClientEvent("faq_link_clicked", { question: faq.question, target: faq.link?.href })}
                                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-400/20 px-4 py-2 text-sm font-medium text-amber-300 transition-colors hover:bg-amber-400/30"
                                        >
                                            {faq.link.text} →
                                        </a>
                                    )}

                                    {faq.note && (
                                        <p className="mt-3 rounded-lg bg-slate-800/50 p-3 text-sm text-amber-300">
                                            {faq.note}
                                        </p>
                                    )}

                                    {faq.hasPopovers && (
                                        <ul className="mt-4 space-y-2">
                                            <li>
                                                <Popover>
                                                    <PopoverTrigger className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-amber-300 transition-colors hover:bg-white/20">
                                                        • Campingplatz Fresenbruch
                                                        <FaCircleInfo className="h-4 w-4" />
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-80 border-slate-700 bg-slate-800">
                                                        <p className="mb-3 text-sm text-slate-300">
                                                            Von unserem Stellplatz einfach nach links ausfahren und der Seestraße bis zum Campingplatz Fresenbruch folgen.
                                                        </p>
                                                        <Image
                                                            src={fresenbruch}
                                                            alt="Campingplatz Fresenbruch"
                                                            className="rounded-lg"
                                                            quality={80}
                                                        />
                                                    </PopoverContent>
                                                </Popover>
                                            </li>
                                            <li>
                                                <Popover>
                                                    <PopoverTrigger className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-amber-300 transition-colors hover:bg-white/20">
                                                        • Campingplatz Düne 6
                                                        <FaCircleInfo className="h-4 w-4" />
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-80 border-slate-700 bg-slate-800">
                                                        <p className="mb-3 text-sm text-slate-300">
                                                            Von unserem Stellplatz nach links ausfahren und der Hauptstraße bis zum Kreisverkehr am Edeka folgen. Im Kreisverkehr nach links abbiegen und bis zum Wellness-Camp Düne 6 folgen.
                                                        </p>
                                                        <Image
                                                            src={wellnessCamp}
                                                            alt="Campingplatz Düne 6"
                                                            className="rounded-lg"
                                                            quality={80}
                                                        />
                                                    </PopoverContent>
                                                </Popover>
                                            </li>
                                        </ul>
                                    )}
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </motion.div>
                ))}
            </Accordion>
        </div>
    );
};

export default FAQ;
